import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Logo from '@/components/Logo';
import EventBanner from '@/components/EventBanner';
import {
  Home,
  Search,
  Calendar,
  User,
  Bell,
  Settings,
  LogOut,
  Stethoscope,
  Users,
  LayoutDashboard,
  FileText,
  Wallet,
  Menu,
  X,
  Activity,
  MapPin,
  Heart,
  MessageCircle,
  Brain,
  ClipboardList,
  Building2,
  Pill,
  Package,
  Coins,
  QrCode,
  Shield,
  Megaphone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const Layout = ({ children }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Navigation items based on role
  const getNavItems = () => {
    if (!user) return [];

    const baseItems = [
      { path: '/dashboard', icon: Home, label: 'Accueil' },
    ];

    switch (user.role) {
      case 'patient':
        return [
          ...baseItems,
          { path: '/search', icon: Search, label: 'Rechercher' },
          { path: '/facilities', icon: Building2, label: 'Établissements' },
          { path: '/pharmacies', icon: Pill, label: 'Pharmacies' },
          { path: '/ai-assistant', icon: Brain, label: 'Assistant IA' },
          { path: '/appointments', icon: Calendar, label: 'RDV' },
          { path: '/chat', icon: MessageCircle, label: 'Messages' },
          { path: '/medical-records', icon: FileText, label: 'Dossier' },
          { path: '/patient/reminders', icon: Bell, label: 'Rappels' },
          { path: '/credits', icon: Coins, label: 'Crédits' },
        ];
      case 'professional':
        return [
          ...baseItems,
          { path: '/pro/appointments', icon: Calendar, label: 'RDV' },
          { path: '/chat', icon: MessageCircle, label: 'Messages' },
          { path: '/ai-assistant', icon: Brain, label: 'Assistant IA' },
          { path: '/pro/home-care', icon: Heart, label: 'Domicile' },
          { path: '/kyc', icon: Shield, label: 'Vérification' },
          { path: '/credits', icon: Coins, label: 'Crédits' },
          { path: '/pro/profile', icon: User, label: 'Mon Profil' },
        ];
      case 'pharmacy':
        return [
          ...baseItems,
          { path: '/pharmacy/dashboard', icon: Package, label: 'Commandes' },
        ];
      case 'facility':
        return [
          ...baseItems,
          { path: '/facility/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
          { path: '/facility/queue', icon: QrCode, label: 'File d\'attente' },
          { path: '/facility/products', icon: Package, label: 'Catalogue' },
          { path: '/facility/events', icon: Megaphone, label: 'Événements' },
          { path: '/facility/professionals', icon: Users, label: 'Équipe' },
          { path: '/kyc', icon: Shield, label: 'Vérification' },
          { path: '/facility/profile', icon: Building2, label: 'Profil' },
          { path: '/chat', icon: MessageCircle, label: 'Messages' },
        ];
      case 'agent':
        return [
          ...baseItems,
          { path: '/search', icon: Search, label: 'Rechercher' },
          { path: '/agent/patients', icon: Users, label: 'Patients' },
        ];
      case 'admin':
        return [
          ...baseItems,
          { path: '/admin/users', icon: Users, label: 'Utilisateurs' },
          { path: '/admin/validations', icon: Stethoscope, label: 'Validations' },
          { path: '/admin/missions', icon: Calendar, label: 'Missions' },
          { path: '/admin/stats', icon: LayoutDashboard, label: 'Stats' },
        ];
      default:
        return baseItems;
    }
  };

  const navItems = getNavItems();

  if (!isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] overflow-x-hidden w-full">
      {/* Event Banner - Shows featured promotions */}
      <EventBanner />
      
      {/* Desktop Header - Glass Effect */}
      <header className="hidden lg:flex header-glass fixed top-0 left-0 right-0 h-16 z-40 px-6 items-center justify-between">
        <Link to="/dashboard" data-testid="header-logo">
          <Logo size="sm" showSlogan={false} />
        </Link>

        <nav className="flex items-center gap-1" data-testid="main-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link ${isActive ? 'active' : ''}`}
                data-testid={`nav-${item.path.replace(/\//g, '-')}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <Link 
            to="/notifications" 
            className="relative p-2.5 rounded-xl hover:bg-slate-100 transition-colors"
            data-testid="notifications-link"
          >
            <Bell className="w-5 h-5 text-slate-600" />
            <span className="notif-badge">3</span>
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-slate-100"
                data-testid="user-menu-trigger"
              >
                <Avatar className="w-9 h-9 avatar-ring">
                  {user?.picture ? (
                    <AvatarImage src={user.picture} alt={user?.full_name} />
                  ) : null}
                  <AvatarFallback className="bg-gradient-to-br from-[#E53935] to-[#D32F2F] text-white font-semibold">
                    {user?.full_name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden lg:block text-left">
                  <span className="text-sm font-semibold text-[#1A237E] block">
                    {user?.full_name}
                  </span>
                  <span className="text-xs text-slate-500 capitalize">
                    {user?.role === 'professional' ? 'Professionnel' : user?.role}
                  </span>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 p-2">
              <div className="px-3 py-3 bg-slate-50 rounded-xl mb-2">
                <p className="font-semibold text-[#1A237E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {user?.full_name}
                </p>
                <p className="text-sm text-slate-500">{user?.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="px-2 py-0.5 text-xs font-semibold bg-[#E53935] text-white rounded-full capitalize">
                    {user?.role === 'professional' ? 'Pro' : user?.role}
                  </span>
                  {user?.is_verified && (
                    <span className="px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-700 rounded-full">
                      Verifie
                    </span>
                  )}
                </div>
              </div>
              <DropdownMenuItem 
                onClick={() => navigate(user?.role === 'facility' ? '/facility/profile' : '/profile')}
                className="rounded-lg cursor-pointer"
                data-testid="profile-menu-item"
              >
                <User className="w-4 h-4 mr-2 text-slate-500" />
                {user?.role === 'facility' ? 'Profil établissement' : 'Mon profil'}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => navigate('/notifications')}
                className="rounded-lg cursor-pointer"
              >
                <Bell className="w-4 h-4 mr-2 text-slate-500" />
                Notifications
              </DropdownMenuItem>
              <DropdownMenuSeparator className="my-2" />
              <DropdownMenuItem 
                onClick={handleLogout} 
                className="rounded-lg cursor-pointer text-[#E53935] focus:text-[#E53935] focus:bg-[#FFEBEE]"
                data-testid="logout-menu-item"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Deconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile/Tablet Header */}
      <header className="lg:hidden header-glass fixed top-0 left-0 right-0 h-14 z-40 px-4 flex items-center justify-between">
        <Link to="/dashboard" data-testid="mobile-header-logo">
          <Logo size="xs" showText={false} variant="icon" />
        </Link>

        <div className="flex items-center gap-2">
          <Link to="/notifications" className="relative p-2">
            <Bell className="w-5 h-5 text-slate-600" />
            <span className="notif-badge text-[10px]">3</span>
          </Link>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-slate-600" />
            ) : (
              <Menu className="w-6 h-6 text-slate-600" />
            )}
          </button>
        </div>
      </header>

      {/* Mobile/Tablet Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm" 
          onClick={() => setMobileMenuOpen(false)}
        >
          <div
            className="absolute right-0 top-14 w-72 bg-white h-[calc(100vh-3.5rem)] shadow-xl rounded-tl-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 bg-gradient-to-br from-[#E53935] to-[#D32F2F]">
              <div className="flex items-center gap-4">
                <Avatar className="w-14 h-14 border-2 border-white/30">
                  {user?.picture ? (
                    <AvatarImage src={user.picture} alt={user?.full_name} />
                  ) : null}
                  <AvatarFallback className="bg-white text-[#E53935] text-xl font-bold">
                    {user?.full_name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="text-white">
                  <p className="font-semibold text-lg">{user?.full_name}</p>
                  <p className="text-sm text-white/80">{user?.email}</p>
                </div>
              </div>
            </div>
            <nav className="p-3">
              <Link
                to={user?.role === 'facility' ? '/facility/profile' : '/profile'}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <User className="w-5 h-5 text-[#2196F3]" />
                <span className="font-medium text-[#1A237E]">
                  {user?.role === 'facility' ? 'Profil établissement' : 'Mon profil'}
                </span>
              </Link>
              <Link
                to="/notifications"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <Bell className="w-5 h-5 text-[#2196F3]" />
                <span className="font-medium text-[#1A237E]">Notifications</span>
              </Link>
              <div className="my-3 border-t border-slate-100" />
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl hover:bg-[#FFEBEE] text-[#E53935] w-full transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Deconnexion</span>
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="pt-14 lg:pt-16 pb-24 lg:pb-8 min-h-screen overflow-x-hidden">
        <div className="w-full max-w-full overflow-x-hidden">
          {children}
        </div>
        
        {/* Footer avec mentions légales - Desktop uniquement */}
        <footer className="hidden lg:block mt-12 border-t border-slate-200 bg-white">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              {/* Colonne Marque */}
              <div>
                <p className="text-[#DC2626] font-semibold text-lg mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  DOCKHTA
                </p>
                <p className="text-slate-500 text-sm mb-3">
                  Votre santé, notre priorité
                </p>
                <p className="text-slate-600 text-sm">
                  <a href="https://dockhta.com" className="text-[#DC2626] hover:underline">www.dockhta.com</a>
                </p>
                <p className="text-slate-500 text-sm mt-2">
                  📞 +1 514 294 6349 | +237 695 06 57 08
                </p>
              </div>
              
              {/* Colonne Annuaires */}
              <div>
                <p className="font-semibold text-slate-900 mb-3">Annuaires</p>
                <div className="space-y-2 text-sm">
                  <Link to="/professionnels" className="block text-slate-600 hover:text-[#DC2626] transition-colors">
                    Trouver un professionnel
                  </Link>
                  <Link to="/etablissements" className="block text-slate-600 hover:text-[#DC2626] transition-colors">
                    Trouver un établissement
                  </Link>
                  <Link to="/catalogue" className="block text-slate-600 hover:text-[#DC2626] transition-colors">
                    Catalogue produits & services
                  </Link>
                  <Link to="/pharmacies" className="block text-slate-600 hover:text-[#DC2626] transition-colors">
                    Pharmacies
                  </Link>
                </div>
              </div>
              
              {/* Colonne Rejoindre */}
              <div>
                <p className="font-semibold text-slate-900 mb-3">Rejoindre DOCKHTA</p>
                <div className="space-y-2 text-sm">
                  <Link to="/rejoindre" className="block text-slate-600 hover:text-[#DC2626] transition-colors">
                    Professionnels de santé
                  </Link>
                  <Link to="/devenir-agent" className="block text-slate-600 hover:text-[#DC2626] transition-colors">
                    ★ Devenir agent terrain
                  </Link>
                  <Link to="/faq" className="block text-slate-600 hover:text-[#DC2626] transition-colors">
                    FAQ
                  </Link>
                  <Link to="/contact" className="block text-slate-600 hover:text-[#DC2626] transition-colors">
                    Contact
                  </Link>
                </div>
              </div>
            </div>
            
            {/* Bas du footer */}
            <div className="pt-6 border-t border-slate-100">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="text-slate-400 text-xs text-center md:text-left">
                  <p>
                    Exploité par <strong>Passerelle numérique inc</strong> (Québec: 1182138157) |
                    Opéré par <strong>AEA SARL</strong> (NIU: M1125181812092)
                  </p>
                  <p className="mt-1">
                    Siège social: EBOLOWA SUD CAMEROUN
                  </p>
                </div>
                <div className="flex flex-wrap justify-center gap-4 text-xs text-slate-500">
                  <Link to="/mentions-legales" className="hover:text-[#DC2626] transition-colors">Mentions légales</Link>
                  <Link to="/privacy" className="hover:text-[#DC2626] transition-colors">Confidentialité</Link>
                  <Link to="/terms" className="hover:text-[#DC2626] transition-colors">CGU</Link>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </main>

      {/* Mobile/Tablet Bottom Navigation - Fixed width items to prevent overflow */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-30 safe-area-inset-bottom">
        <div className="flex items-center justify-between h-16 px-2">
          {navItems.slice(0, 5).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center py-1 min-w-[52px] flex-1 max-w-[72px] rounded-xl transition-all ${
                  isActive 
                    ? 'text-[#E53935]' 
                    : 'text-slate-500 hover:text-[#1A237E]'
                }`}
                data-testid={`mobile-nav-${item.path.replace(/\//g, '-')}`}
              >
                <div className={`p-1.5 rounded-xl transition-colors ${isActive ? 'bg-[#FFEBEE]' : ''}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <span className="text-[9px] font-medium mt-0.5 truncate max-w-full text-center">{item.label}</span>
              </Link>
            );
          })}
          <Link
            to={user?.role === 'facility' ? '/facility/profile' : '/profile'}
            className={`flex flex-col items-center justify-center py-1 min-w-[52px] flex-1 max-w-[72px] rounded-xl transition-all ${
              (location.pathname === '/profile' || location.pathname === '/facility/profile')
                ? 'text-[#E53935]' 
                : 'text-slate-500 hover:text-[#1A237E]'
            }`}
            data-testid="mobile-nav-profile"
          >
            <div className={`p-1.5 rounded-xl transition-colors ${(location.pathname === '/profile' || location.pathname === '/facility/profile') ? 'bg-[#FFEBEE]' : ''}`}>
              <User className="w-5 h-5" />
            </div>
            <span className="text-[9px] font-medium mt-0.5">Profil</span>
          </Link>
        </div>
      </nav>
    </div>
  );
};

export default Layout;
